function normalizeApiDateTime(iso: string): string {
  const value = iso.trim();
  if (!value) return value;
  // API datetimes are stored in UTC. Some ORM/Pydantic paths serialize UTC
  // values without a trailing offset (e.g. `2026-05-21T18:30:00`). Browsers
  // interpret those as local time, causing a +timezone-offset display shift.
  // Date-only strings are intentionally left alone.
  if (/^\d{4}-\d{2}-\d{2}T/.test(value) && !/(Z|[+-]\d{2}:?\d{2})$/.test(value)) {
    return value + 'Z';
  }
  return value;
}

function displayDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(normalizeApiDateTime(iso));
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatterOptions(timeZone?: string | null): Intl.DateTimeFormatOptions {
  return timeZone ? { timeZone } : {};
}

export function formatEpochMs(iso: string | null | undefined): number {
  return displayDate(iso)?.getTime() ?? Infinity;
}

export function formatTime(iso: string | null | undefined, timeZone?: string | null): string {
  const date = displayDate(iso);
  if (!date) return '—';
  return date.toLocaleTimeString(undefined, {
    ...formatterOptions(timeZone),
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatShortDateTime(iso: string | null | undefined, timeZone?: string | null): string {
  const date = displayDate(iso);
  if (!date) return '—';
  return date.toLocaleString(undefined, {
    ...formatterOptions(timeZone),
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDurationFromSeconds(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '—';
  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
}

export function formatDurationFromMinutes(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return '—';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours) return `${hours}h`;
  return `${mins}m`;
}

export function formatDistanceMeters(meters: number | null | undefined): string {
  if (!meters || meters <= 0) return '—';
  const miles = meters / 1609.344;
  return `${miles.toFixed(miles >= 10 ? 0 : 1)} mi`;
}

export function routeStatusLabel(status: string | null | undefined): string {
  if (status === 'fresh') return 'Fresh';
  if (status === 'stale') return 'Needs update';
  if (status === 'failed') return 'Setup needed';
  return 'Not calculated';
}

export function routeStatusClass(status: string | null | undefined): string {
  if (status === 'fresh') return 'border-eva-teal/30 bg-eva-teal/10 text-eva-teal';
  if (status === 'stale') return 'border-bracket-amber/40 bg-bracket-amber/10 text-bracket-amber';
  if (status === 'failed') return 'border-red-200 bg-red-50 text-red-700';
  return 'border-gray-200 bg-gray-50 text-gray-600';
}
